import { useEffect, useMemo, useRef, useState } from "react"
import { Badge, Box, HStack, Icon, Input, Text, VStack } from "@chakra-ui/react"
import { FaPencil, FaRotateLeft } from "react-icons/fa6"
import { Switch } from "../../chakra/Switch"
import { Chip, StatusTag, chipStyle } from "../Chip"
import { TitleWithChips } from "../TitleWithChips"
import { Tooltip } from "../../chakra/Tooltip"
import { formatChipLabel } from "./alertRuleFormat"
import { SeverityTag } from "./alertSummary"
import { useAlertRuleControls } from "./useAlertRuleControls"
import type { AlertConditionGroup } from "./alertConditions"

export { formatChipLabel } from "./alertRuleFormat"

// --- Alert Rule Configuration Types ---

export interface AlertTemplate {
  id: string
  name: string
  description: string | null
  metric: string | null
  conditionType: string | null
  operator: string | null
  threshold: number | null
  timeWindowSeconds: number | null
  severity: string
  evaluationTier: string
  scopeLevel: string | null
  overridable: boolean
  thresholdUnit: string | null
  thresholdDirection: string | null
  enabled: boolean
  isGlobal: boolean
  /** STANDARD (platform-defined) or CUSTOM (defined by a customer on one Asset) */
  origin?: string | null
  ownerAssetId?: string | null
  /** A Custom Alert's conditions, when it has more than a single threshold */
  conditionGroup?: AlertConditionGroup | null
  /** The Asset Classes a Standard Alert applies to; null or absent for every class */
  assetClasses?: string[] | null
}

export interface AlertAssignment {
  id: string
  templateId: string
  assetId: string | null
  systemId: string | null
  enabled: boolean
  thresholdOverride: number | null
  timeWindowOverrideSeconds: number | null
  paramValues: Record<string, any> | null
  createdAt: string | null
  updatedAt: string | null
}

export interface AlertRuleState {
  template: AlertTemplate
  assignment: AlertAssignment | null
  isEnabled: boolean
  isOverridden: boolean
  effectiveThreshold: number | null
  /** The Asset the rule judges, when a page lists rules for several (an Asset and its Modules) */
  targetAssetId?: string
}

export interface AlertRuleCardProps {
  rule: AlertRuleState
  onToggle: (enabled: boolean) => void | Promise<void>
  onUpdateThreshold: (value: number) => void | Promise<void>
  onUpdateTimeWindow: (value: number) => void | Promise<void>
  onReset: () => void | Promise<void>
}

// The site's outlined chips (components/Chip), in the alert tags' colours
const NEUTRAL_CHIP = { bg: "transparent", color: "#545454", border: "#D0D0D0" }
const VALUE_CHIP = { bg: "white", color: "#2D3748", border: "#D0D0D0" }
const CUSTOMISED_CHIP = { bg: "rgba(161,112,217,.08)", color: "#7B52AB", border: "#a170d9" }

/** Inline editable value — click text to reveal input, Enter/blur to confirm, Escape to cancel */
function InlineEditableValue({
  value,
  suffix,
  prefix,
  disabled,
  onCommit,
  ariaLabel,
  min,
  max,
}: {
  value: string
  suffix?: string
  prefix?: string
  disabled?: boolean
  onCommit: (newValue: string) => void
  ariaLabel: string
  min?: number
  max?: number
}) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!editing) {
      setEditValue(value)
    }
  }, [value, editing])

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const handleClick = () => {
    if (disabled) return
    setEditing(true)
    setEditValue(value)
  }

  const handleConfirm = () => {
    setEditing(false)
    if (editValue !== value && editValue.trim()) {
      let finalValue = editValue.trim()
      const num = parseFloat(finalValue)
      if (!isNaN(num)) {
        const clamped = Math.min(Math.max(num, min ?? -Infinity), max ?? Infinity)
        finalValue = clamped.toString()
      }
      onCommit(finalValue)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleConfirm()
    } else if (e.key === "Escape") {
      setEditing(false)
      setEditValue(value)
    }
  }

  if (editing) {
    return (
      <HStack gap={0} display="inline-flex" align="center">
        {prefix && <Text fontSize="xs" mr={1}>{prefix}</Text>}
        <Input
          ref={inputRef}
          size="2xs"
          h="20px"
          w="52px"
          px={1}
          fontSize="xs"
          type="number"
          textAlign="center"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleConfirm}
          onKeyDown={handleKeyDown}
          aria-label={ariaLabel}
          min={min}
          max={max}
          borderColor="blue.300"
          _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
        />
        {suffix && <Text fontSize="xs" ml={1}>{suffix}</Text>}
      </HStack>
    )
  }

  return (
    <HStack
      as="span"
      display="inline-flex"
      gap={1}
      align="center"
      cursor={disabled ? "default" : "pointer"}
      onClick={handleClick}
      role={disabled ? undefined : "button"}
      aria-label={disabled ? undefined : ariaLabel}
      _hover={disabled ? undefined : { color: "#0bc5ea" }}
      transition="color 0.1s"
    >
      <Text
        as="span"
        fontSize="xs"
        borderBottom={disabled ? "none" : "1px dashed"}
        borderColor="currentColor"
        transition="all 0.1s"
      >
        {prefix && <Text as="span">{prefix} </Text>}
        {value}{suffix ?? ""}
      </Text>
      {!disabled && (
        <Icon as={FaPencil} boxSize="10px" />
      )}
    </HStack>
  )
}

export function AlertRuleCard({ rule, onToggle, onUpdateThreshold, onUpdateTimeWindow, onReset }: AlertRuleCardProps) {
  const { template, isEnabled, effectiveThreshold } = rule
  const {
    isToggling,
    resetting,
    showAsOverridden,
    canEdit,
    displayThreshold,
    displayTimeWindow,
    timeWindowUnit,
    handleToggle,
    handleThresholdCommit,
    handleTimeWindowCommit,
    handleReset,
  } = useAlertRuleControls(rule, { onToggle, onUpdateThreshold, onUpdateTimeWindow, onReset })


  const configChip = useMemo(() => {
    if (showAsOverridden) {
      return (
        <Badge {...chipStyle(CUSTOMISED_CHIP)} gap={1.5}>
          Customised
          {!isEnabled && (
            <Tooltip content="Reset to default values">
              <Box
                as="button"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                borderRadius="full"
                p="2px"
                lineHeight={1}
                _hover={{ bg: "rgba(161,112,217,.22)", color: "#553C9A" }}
                transition="all 0.15s ease"
                onClick={(e) => {
                  e.stopPropagation()
                  handleReset()
                }}
                aria-label={`Reset ${template.name} to defaults`}
                disabled={resetting}
              >
                <Icon as={FaRotateLeft} boxSize="9px" />
              </Box>
            </Tooltip>
          )}
        </Badge>
      )
    }

    if (template.isGlobal) {
      return (
        <Chip colours={NEUTRAL_CHIP}>Default</Chip>
      )
    }

    return null
  }, [showAsOverridden, template.isGlobal, isEnabled, resetting, handleReset, template.name])


  return (
    <Box
      p={4}
      borderWidth="1px"
      borderRadius="md"
      bg="bg.panel"
      transition="all 0.15s"
    >
      <VStack gap={2} align="stretch">
        {/* Row 1: the name with its chips beside it, lined up across the list (AlignedTitles),
            and the Active/Inactive tag + switch on the right */}
        <HStack justify="space-between" align="center" gap={6}>
          <TitleWithChips
            title={template.name}
            chips={
              <>
                <SeverityTag severity={template.severity} />
                {configChip}

                {/* Editable parameters placed right next to the chips as chips */}
                {template.overridable && effectiveThreshold != null ? (
                  <Tooltip
                    content="Deactivate rule to edit parameters"
                    disabled={canEdit}
                  >
                    <HStack gap={2} align="center">
                      <Badge {...chipStyle(VALUE_CHIP)}>
                        <InlineEditableValue
                          value={displayThreshold}
                          prefix={formatChipLabel(template.thresholdDirection ?? "Above")}
                          suffix={template.thresholdUnit ?? ""}
                          disabled={!canEdit}
                          onCommit={handleThresholdCommit}
                          ariaLabel={`Threshold for ${template.name}`}
                          min={0}
                          max={template.thresholdUnit === "%" ? 100 : undefined}
                        />
                      </Badge>
                      {displayTimeWindow != null && (
                        <Badge {...chipStyle(VALUE_CHIP)}>
                          <InlineEditableValue
                            value={displayTimeWindow}
                            suffix={`${timeWindowUnit} Window`}
                            disabled={!canEdit}
                            onCommit={handleTimeWindowCommit}
                            ariaLabel={`Time window for ${template.name}`}
                            min={1}
                          />
                        </Badge>
                      )}
                    </HStack>
                  </Tooltip>
                ) : (
                  <Chip colours={NEUTRAL_CHIP}>{formatChipLabel("Event-based")}</Chip>
                )}
              </>
            }
          />

          <HStack gap={4} align="center" flexShrink={0}>
            <StatusTag active={isEnabled} />
            <Switch
              checked={isEnabled}
              disabled={isToggling}
              onCheckedChange={(details) => handleToggle(details.checked)}
              size="sm"
            />
          </HStack>
        </HStack>

        {/* Row 3: Description */}
        {template.description && (
          <Text fontSize="sm" color="fg.muted">
            {template.description}
          </Text>
        )}
      </VStack>
    </Box>
  )
}

export default AlertRuleCard
